import {datadogRum} from '@datadog/browser-rum';

import {assert} from 'Asserts';
import {distribution} from 'metrics/Metrics';
import {
  getProfilerEventValue,
  measureTimingBetweenEvents,
  processTimingEvent,
} from 'performance/BenchlingProfiler';

import {EventMetadata, NOT_SPECIFIED, ProfilerEvent, ProfilerEventDomain} from './ProfilerConstants';

/* Should bump version anytime there's a change to logging events or processing them,
 * so that on Datadog, we can quickly see which custom actions have outdated logic or tags
 */
const PROFILER_NOTEBOOK_EVENTS_VERSION = 5;

export enum ProfilerNotebookFeatures {
  TABLE_EXPAND = 'tableExpand',
  TABLE_COLLAPSE = 'tableCollapse',
  TABLE_REVALIDATE = 'tableRevalidate',
  TABLE_SUBMIT = 'tableSubmit',
  FETCH_SAMPLES = 'fetchSamples',
  ATTACHMENT_UPLOAD = 'attachmentUpload',
  AT_MENTION_SEARCH = 'atMentionSearch',
  CONTENT_RENDERING = 'contentRendering',
  ENTRY_EXPORT = 'entryExport',
}

const FEATURE_TO_PROCESSING_FUNCTION: {[key in ProfilerNotebookFeatures]: Function} = {
  [ProfilerNotebookFeatures.TABLE_EXPAND]: processTableExpand,
  [ProfilerNotebookFeatures.TABLE_COLLAPSE]: processTableCollapse,
  [ProfilerNotebookFeatures.TABLE_REVALIDATE]: processRevalidateTable,
  [ProfilerNotebookFeatures.TABLE_SUBMIT]: processSubmitTable,
  [ProfilerNotebookFeatures.FETCH_SAMPLES]: processFetchSamples,
  [ProfilerNotebookFeatures.ATTACHMENT_UPLOAD]: processAttachmentUpload,
  [ProfilerNotebookFeatures.AT_MENTION_SEARCH]: processAtMentionSearch,
  [ProfilerNotebookFeatures.CONTENT_RENDERING]: processContentRendering,
  [ProfilerNotebookFeatures.ENTRY_EXPORT]: processEntryExport,
};

enum ProfilerNotebookActions {
  TABLE_EXPAND_STARTED = 'tableExpandStarted',
  TABLE_EXPAND_FINISHED = 'tableExpandFinished',
  TABLE_COLLAPSE_STARTED = 'tableCollapseStarted',
  TABLE_COLLAPSE_FINISHED = 'tableCollapseFinished',
  TABLE_REVALIDATE_STARTED = 'tableRevalidateStarted',
  TABLE_REVALIDATE_FINISHED = 'tableRevalidateFinished',
  TABLE_SUBMIT_STARTED = 'tableSubmitStarted',
  TABLE_SUBMIT_FINISHED = 'tableSubmitFinished',
  FETCH_SAMPLES_STARTED = 'fetchSamplesStarted',
  FETCH_SAMPLES_FINISHED = 'fetchSamplesFinished',
  ATTACHMENT_UPLOAD_STARTED = 'attachmentUploadStarted',
  ATTACHMENT_UPLOAD_FINISHED = 'attachmentUploadFinished',
  AT_MENTION_SEARCH_STARTED = 'atMentionSearchStarted',
  AT_MENTION_SEARCH_FINISHED = 'atMentionSearchFinished',
  CONTENT_RENDER_STARTED = 'contentRenderStarted',
  CONTENT_RENDER_FINISHED = 'contentRenderFinished',
  ENTRY_EXPORT_STARTED = 'entryExportStarted',
  ENTRY_EXPORT_FINISHED = 'entryExportFinished',
}

export const TABLE_EXPAND_STARTED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.NOTEBOOK,
  feature: ProfilerNotebookFeatures.TABLE_EXPAND,
  action: ProfilerNotebookActions.TABLE_EXPAND_STARTED,
};

export const TABLE_EXPAND_FINISHED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.NOTEBOOK,
  feature: ProfilerNotebookFeatures.TABLE_EXPAND,
  action: ProfilerNotebookActions.TABLE_EXPAND_FINISHED,
};

export const TABLE_COLLAPSE_STARTED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.NOTEBOOK,
  feature: ProfilerNotebookFeatures.TABLE_COLLAPSE,
  action: ProfilerNotebookActions.TABLE_COLLAPSE_STARTED,
};

export const TABLE_COLLAPSE_FINISHED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.NOTEBOOK,
  feature: ProfilerNotebookFeatures.TABLE_COLLAPSE,
  action: ProfilerNotebookActions.TABLE_COLLAPSE_FINISHED,
};

export const TABLE_REVALIDATE_STARTED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.NOTEBOOK,
  feature: ProfilerNotebookFeatures.TABLE_REVALIDATE,
  action: ProfilerNotebookActions.TABLE_REVALIDATE_STARTED,
};

export const TABLE_REVALIDATE_FINISHED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.NOTEBOOK,
  feature: ProfilerNotebookFeatures.TABLE_REVALIDATE,
  action: ProfilerNotebookActions.TABLE_REVALIDATE_FINISHED,
};

export const TABLE_SUBMIT_STARTED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.NOTEBOOK,
  feature: ProfilerNotebookFeatures.TABLE_SUBMIT,
  action: ProfilerNotebookActions.TABLE_SUBMIT_STARTED,
};

export const TABLE_SUBMIT_FINISHED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.NOTEBOOK,
  feature: ProfilerNotebookFeatures.TABLE_SUBMIT,
  action: ProfilerNotebookActions.TABLE_SUBMIT_FINISHED,
};

export const FETCH_SAMPLES_STARTED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.NOTEBOOK,
  feature: ProfilerNotebookFeatures.FETCH_SAMPLES,
  action: ProfilerNotebookActions.FETCH_SAMPLES_STARTED,
};

export const FETCH_SAMPLES_FINISHED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.NOTEBOOK,
  feature: ProfilerNotebookFeatures.FETCH_SAMPLES,
  action: ProfilerNotebookActions.FETCH_SAMPLES_FINISHED,
};

export const ATTACHMENT_UPLOAD_STARTED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.NOTEBOOK,
  feature: ProfilerNotebookFeatures.ATTACHMENT_UPLOAD,
  action: ProfilerNotebookActions.ATTACHMENT_UPLOAD_STARTED,
};

export const ATTACHMENT_UPLOAD_FINISHED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.NOTEBOOK,
  feature: ProfilerNotebookFeatures.ATTACHMENT_UPLOAD,
  action: ProfilerNotebookActions.ATTACHMENT_UPLOAD_FINISHED,
};

export const AT_MENTION_SEARCH_STARTED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.NOTEBOOK,
  feature: ProfilerNotebookFeatures.AT_MENTION_SEARCH,
  action: ProfilerNotebookActions.AT_MENTION_SEARCH_STARTED,
};

export const AT_MENTION_SEARCH_FINISHED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.NOTEBOOK,
  feature: ProfilerNotebookFeatures.AT_MENTION_SEARCH,
  action: ProfilerNotebookActions.AT_MENTION_SEARCH_FINISHED,
};

export const CONTENT_RENDER_STARTED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.NOTEBOOK,
  feature: ProfilerNotebookFeatures.CONTENT_RENDERING,
  action: ProfilerNotebookActions.CONTENT_RENDER_STARTED,
};

export const CONTENT_RENDER_FINISHED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.NOTEBOOK,
  feature: ProfilerNotebookFeatures.CONTENT_RENDERING,
  action: ProfilerNotebookActions.CONTENT_RENDER_FINISHED,
};

export const ENTRY_EXPORT_STARTED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.NOTEBOOK,
  feature: ProfilerNotebookFeatures.ENTRY_EXPORT,
  action: ProfilerNotebookActions.ENTRY_EXPORT_STARTED,
};

export const ENTRY_EXPORT_FINISHED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.NOTEBOOK,
  feature: ProfilerNotebookFeatures.ENTRY_EXPORT,
  action: ProfilerNotebookActions.ENTRY_EXPORT_FINISHED,
};

export function processNotebookEvents(profilerEvent: Pick<ProfilerEvent, 'feature' | 'action'>): void {
  const feature = profilerEvent.feature as ProfilerNotebookFeatures;
  assert(
    Object.values(ProfilerNotebookFeatures).includes(feature),
    `Unexpected profiler feature: ${profilerEvent.feature}`
  );
  FEATURE_TO_PROCESSING_FUNCTION[feature]();
}

function processTableExpand(): void {
  const measurement = measureTimingBetweenEvents(TABLE_EXPAND_STARTED_EVENT, TABLE_EXPAND_FINISHED_EVENT);
  if (measurement == null) {
    return;
  }

  const {time} = measurement;
  datadogRum.addAction('Table: Expanded', {time});
}

function processTableCollapse(): void {
  const measurement = measureTimingBetweenEvents(TABLE_COLLAPSE_STARTED_EVENT, TABLE_COLLAPSE_FINISHED_EVENT);
  if (measurement == null) {
    return;
  }

  const {time} = measurement;
  datadogRum.addAction('Table: Collapsed', {time});
}

function processFetchSamples(): void {
  const startedEvent = getProfilerEventValue(FETCH_SAMPLES_STARTED_EVENT);
  const measurement = measureTimingBetweenEvents(FETCH_SAMPLES_STARTED_EVENT, FETCH_SAMPLES_FINISHED_EVENT);
  if (measurement == null) {
    return;
  }

  const {time} = measurement;
  datadogRum.addAction('Fetched Samples', {
    time,
    sampleCount: startedEvent.extra?.sampleCount,
    tableKind: startedEvent.extra?.tableKind,
    isUsingBulkQueries: startedEvent.extra?.isUsingBulkQueries,
  });
}

function processEntryExport(): void {
  processTimingEvent(
    ENTRY_EXPORT_STARTED_EVENT,
    ENTRY_EXPORT_FINISHED_EVENT,
    'Exported entry',
    'perf.notebook.entry_export',
    PROFILER_NOTEBOOK_EVENTS_VERSION
  );
}

export const mimeTypeToFileTypeName: Record<string, string> = {
  'application/json': 'json',
  'application/pdf': 'pdf',
  'application/vnd.ms-excel': 'xls',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
  'application/msword': 'doc',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
  'text/csv': 'csv',
  'text/html': 'html',
  'text/plain': 'txt',
  'text/tab-separated-values': 'tsv',
  'text/xml': 'xml',
  'application/zip': 'zip',
  'application/vnd.rar': 'rar',
  'application/x-7z-compressed': '7z',
  'image/gif': 'gif',
  'image/jpeg': 'jpeg',
  'image/png': 'png',
  'video/mp4': 'mp4',
  'audio/mpeg': 'mp3',
};

function getFileSizeBucket(size?: number): string {
  if (size === undefined) {
    return NOT_SPECIFIED;
  } else if (size < 4 * 1000 * 1000) {
    return 'small';
  } else if (size < 10 * 1000 * 1000) {
    return 'medium';
  } else {
    return 'large';
  }
}

export function composeAttachmentUploadEventMetadata(file: File): EventMetadata {
  const fileSizeBucket = getFileSizeBucket(file.size);
  const fileTypeName = mimeTypeToFileTypeName[file.type] ?? 'unknown';
  return {
    distributionMetadata: {
      file_type: fileTypeName,
      file_size_bucket: fileSizeBucket,
    },
    actionMetadata: {
      file_type: fileTypeName,
      file_size_bucket: fileSizeBucket,
    },
  };
}

function processAttachmentUpload(): void {
  processTimingEvent(
    ATTACHMENT_UPLOAD_STARTED_EVENT,
    ATTACHMENT_UPLOAD_FINISHED_EVENT,
    'Uploaded attachment',
    'perf.attachment_upload',
    PROFILER_NOTEBOOK_EVENTS_VERSION
  );
}

function processContentRendering(): void {
  const measurement = measureTimingBetweenEvents(CONTENT_RENDER_STARTED_EVENT, CONTENT_RENDER_FINISHED_EVENT);
  if (measurement == null) {
    return;
  }

  const {time, endEvent} = measurement;
  const eventMetadata: EventMetadata | undefined = endEvent?.extra as EventMetadata;

  distribution('perf.notebook.content_rendered', time, eventMetadata?.distributionMetadata || {});
}

function processRevalidateTable(): void {
  processTimingEvent(
    TABLE_REVALIDATE_STARTED_EVENT,
    TABLE_REVALIDATE_FINISHED_EVENT,
    'Revalidated table',
    'perf.revalidate_table',
    PROFILER_NOTEBOOK_EVENTS_VERSION
  );
}

function processSubmitTable(): void {
  processTimingEvent(
    TABLE_SUBMIT_STARTED_EVENT,
    TABLE_SUBMIT_FINISHED_EVENT,
    'Submitted table',
    'perf.submit_table',
    PROFILER_NOTEBOOK_EVENTS_VERSION
  );
}

function processAtMentionSearch(): void {
  const finishedEvent = getProfilerEventValue(AT_MENTION_SEARCH_FINISHED_EVENT);
  const measurement = measureTimingBetweenEvents(
    AT_MENTION_SEARCH_STARTED_EVENT,
    AT_MENTION_SEARCH_FINISHED_EVENT
  );
  if (measurement == null) {
    return;
  }

  const {time} = measurement;

  datadogRum.addAction('At Mention Search', {
    time,
    fileId: finishedEvent.extra?.fileId,
    hasNameCount: finishedEvent.extra?.hasNameCount,
    hasCreatorCount: finishedEvent.extra?.hasCreatorCount,
    resultsCount: finishedEvent.extra?.resultsCount,
  });
}
